import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { shiftCropToFaceCentered } from '@ff-os/shared-utils/crop/shiftCropToFaceCentered';
import { sliceCropToAspectIncludeFaces } from '@ff-os/shared-utils/crop/sliceCropToIncludeFaces';
import { sliceCropToAspect } from '@ff-os/shared-utils/crop/sliceCropToSize';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import { useWindowSize } from '@react-hook/window-size';
import type { FaceDetection } from 'ff-face-detection';
import { noop } from 'lodash';
import { WindowStackItem } from '../../../context/window-stack/WindowStackItem';
import { useContain } from '../../../hooks/useContain';
import { AiStudioButton } from '../../ai-studio/AiStudioButton';
import { AiStudioWindow } from '../../ai-studio/AiStudioWindow';
import { LightSwitch } from '../../light-switch/LightSwitch';
import { ConfirmModal } from '../../modal/ConfirmModal';
import { CropTool } from '../crop-tool/CropTool';
import { PhotoEditButtonGroup } from '../photo-edit-button-group/PhotoEditButtonGroup';
import { rotateAndSliceCrop } from '../rotateAndSliceCrop';
import styles from './PhotoEditModal.module.scss';

// Liberal estimation of the total of margin + padding + size of components surrounding
// the `styles.container` div.
const offsetX = 50;
const offsetY = 230;

export const PhotoEditModal = ({
    isOpen, close, overlayUrl, fileName,
    applyCropBox, activeCropBox,
    imageSize, clip,
    aspectRatio: defaultAspectRatio,
    filter, updateFilter,
    children, confirm,
    enableAiStudio, aiStudioStartupAppId,
    autoFaceCenter, detections,
    telemetryCategory, pluginId,
    uploadFiles, hasPhotoFunBorder
}: Props) => {

    const { t } = useTranslation();
    const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
    const [cropBox, setCropBox] = useState<Area>(activeCropBox);
    const [enableFaceCenter, setEnableFaceCenter] = useState<boolean>(autoFaceCenter ?? false);
    const [boundingBox, setBoundingBox] = useState<Size>({
        width: window.innerWidth, height: window.innerHeight
    });

    const [openAiStudio, setOpenAiStudio] = useState(false);

    const canRotate = aspectRatio && aspectRatio !== 1;

    const [windowWidth, windowHeight] = useWindowSize();
    const contentSize = useContain(
        boundingBox, imageSize.width / imageSize.height);

    useEffect(() => {
        setBoundingBox({
            width: Math.min(imageSize.width, 0.8 * windowWidth - offsetX),
            height: Math.min(imageSize.height, 0.8 * windowHeight - offsetY)
        });
    }, [windowWidth, windowHeight, imageSize]);

    useEffect(() => {

        setAspectRatio(defaultAspectRatio);

        let cropBox = defaultAspectRatio
            ? sliceCropToAspect(activeCropBox, defaultAspectRatio)
            : activeCropBox;

        if (autoFaceCenter && detections) {
            cropBox = shiftCropToFaceCentered(cropBox, detections, imageSize);
        }

        setCropBox(cropBox);
    }, [activeCropBox, defaultAspectRatio, imageSize, detections, autoFaceCenter]);

    const onCropBoxChange = (newCropBox: Area) => {
        setEnableFaceCenter(false);
        setCropBox(newCropBox);
    };

    const onConfirm = useCallback(() => {

        if (enableFaceCenter && detections && aspectRatio) {
            applyCropBox(sliceCropToAspectIncludeFaces(cropBox, aspectRatio, imageSize, detections));
        } else {
            applyCropBox(cropBox);
        }

        confirm && confirm();
        close();
    }, [confirm, close, applyCropBox, cropBox, aspectRatio, detections, enableFaceCenter, imageSize]);

    const rotateAspectRatio = useCallback(() => {
        const newAspectRatio = 1 / aspectRatio!;
        setAspectRatio(newAspectRatio);
        setCropBox(rotateAndSliceCrop(cropBox, imageSize, newAspectRatio));
    }, [aspectRatio, cropBox, imageSize]);

    return (
        <ConfirmModal
            className={styles.modal}
            width='auto' height='100%'
            title={t('edit_modal.title')}
            active={isOpen}
            onConfirm={onConfirm}
            onReject={() => close()}>

            <div className={styles.container}>
                <div style={contentSize}>
                    <CropTool
                        overlayUrl={overlayUrl}
                        containerSize={contentSize}
                        activeCropBox={cropBox}
                        imageSize={imageSize}
                        onChange={onCropBoxChange}
                        clip={clip}
                        filter={filter}
                        aspectRatio={aspectRatio}
                        hasPhotoFunBorder={hasPhotoFunBorder}>
                        {children}
                    </CropTool>
                </div>

                <PhotoEditButtonGroup>
                    {canRotate && (
                        <PhotoEditButtonGroup.Button
                            type='rotate'
                            onClick={rotateAspectRatio}
                            icon='general-ui-rotate-image'>
                            {t('commons.rotate')}
                        </PhotoEditButtonGroup.Button>
                    )}

                    {updateFilter && (
                        <PhotoEditButtonGroup.Button
                            onClick={() => updateFilter(filter !== 'grayscale' ? 'grayscale' : null)}>
                            <LightSwitch checked={filter === 'grayscale'} onChange={noop} />
                            {t('photo_filters.grayscale')}
                        </PhotoEditButtonGroup.Button>
                    )}

                    {(openAiStudio && uploadFiles) && (
                        <WindowStackItem>
                            <AiStudioWindow
                                onClose={() => setOpenAiStudio(false)}
                                uploadFiles={uploadFiles}
                                aiStudioStartupAppId={aiStudioStartupAppId}
                                telemetryCategory={telemetryCategory}
                                image={{
                                    name: fileName ?? '',
                                    url: overlayUrl
                                }}
                                pluginId={pluginId ?? ''}
                            />
                        </WindowStackItem>
                    )}

                    {(enableAiStudio && uploadFiles) && (
                        <AiStudioButton onClick={() => setOpenAiStudio(true)} />
                    )}
                </PhotoEditButtonGroup>
            </div>

        </ConfirmModal>
    );
};

type Props = PropsWithChildren<{

    fileName?: string;
    overlayUrl: string;
    imageSize: Size;
    activeCropBox: Area;
    applyCropBox: (cropBox: Area) => void;

    isOpen: boolean;
    close: () => void;

    clip?: Clip;
    aspectRatio?: number;

    filter: string | null;
    updateFilter: (filter: string | null) => void;

    confirm?: () => void;
    uploadFiles?: (files: File[]) => void;

    telemetryCategory?: string;
    pluginId?: string;

    enableAiStudio?: boolean;
    aiStudioStartupAppId?: string;

    autoFaceCenter?: boolean;
    detections?: FaceDetection[];

    hasPhotoFunBorder?: boolean;
}>;